import { spaces } from '@99designs/common/core/settings';
import { StructuredBriefFieldsInput } from '__generated__/types';
import BriefPackage, { Props as BriefPackageProps } from 'projects/components/BriefPackage';
import Header from 'projects/components/Header';
import HeaderUserLayout from 'projects/components/HeaderUserLayout';
import Link from 'projects/components/Link';
import Stack from 'projects/libs/core-components/Stack/Stack';
import { StepState } from 'projects/util/step';
import React, { useContext } from 'react';
import styled from 'styled-components';
import BriefContext from './BriefContext';
import { Designer as DesignerInterface } from './User';

interface Props<T> {
    step: StepState<T>;
    designers: Array<DesignerInterface>;
    briefPackage?: BriefPackageProps;
}

const Navigation = styled.div`
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    margin-bottom: ${spaces.space8}px;
`;

const BackButton = styled.div`
    position: absolute;
    left: 0;
`;

const StepCounter = styled.div`
    align-self: flex-end;
`;

const StyledPartnerLogo = styled.div`
    text-align: left;

    img {
        max-width: 160px;
    }
`;

const StyledStepHeader = styled.div`
    max-width: 1000px;
    margin: auto;
`;

export default function StepHeader<T>({ step, designers, briefPackage }: Props<T>) {
    const { schema } = useContext(BriefContext);
    const partnerLogo = schema ? PartnerLogo(schema) : undefined;

    return (
        <StyledStepHeader>
            <Header title={`${step.currentStep().title} | 99designs`} textAlignment={'left'}>
                <div data-test-id="step-header">
                    <Navigation>
                        <BackButton>
                            {step.hasStepBefore() && (
                                <Link to={step.prevUrl()} variations={['back', 'big-target']}>
                                    {__('Back')}
                                </Link>
                            )}
                        </BackButton>

                        <StepCounter>
                            <strong>{step.number.toString().padStart(2, '0')} </strong>
                            <p className={'paragraph, paragraph--soft paragraph--inline'}>
                                /{' '}
                                {step
                                    .stepCount()
                                    .toString()
                                    .padStart(2, '0')}
                            </p>
                        </StepCounter>
                    </Navigation>

                    <Stack space="space8">
                        <h2 className={'heading heading--h2 heading--no-margin'}>
                            {step.currentStep().title}
                        </h2>

                        {!step.isDesignerStep() && designers.length > 0 && (
                            <HeaderUserLayout users={designers} />
                        )}

                        {partnerLogo && (
                            <StyledPartnerLogo>
                                <img src={partnerLogo} />
                            </StyledPartnerLogo>
                        )}

                        {briefPackage && <BriefPackage {...briefPackage} />}
                    </Stack>
                </div>
            </Header>
        </StyledStepHeader>
    );
}

function PartnerLogo(schema: keyof StructuredBriefFieldsInput): string | undefined {
    switch (schema) {
        case 'nationBuilder':
            return require('images/projects/create/partners/nationbuilder.png');
        default:
            return undefined;
    }
}
